/**
 * One clear screen when the server refuses a person the organisation.
 *
 * A server answer of 403 with ``{"code": "access_refused", "detail": …}``
 * means every request for this organisation's data will be refused the
 * same way (a rule of the organisation: where they are, what is on their
 * account, how old their sign-in is). Without this the page fills with
 * one error per request. Here the first such answer puts up a notice
 * with the reason and, when the answer carries ``action: {href, label}``,
 * a button to the page that settles it.
 *
 * Installed once (main.tsx). It only reads answers; it changes none.
 */
const ID = "afri-access-refused";

const STYLE = `
#${ID} {
  position: fixed;
  inset: 0;
  z-index: 2147483600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--color-neutral-background, #fff);
  color: var(--color-neutral-content, #1f1f1f);
  font-family: var(--font-family-body, system-ui, sans-serif);
}
#${ID} > div {
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
#${ID} h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}
#${ID} p {
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  color: var(--color-neutral-content-subtle, #4b5563);
}
#${ID} nav {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
#${ID} a,
#${ID} button {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--color-neutral-border, #d9d9d4);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}
#${ID} a[data-primary] {
  border-color: var(--color-primary-surface, #3b4ea8);
  background: var(--color-primary-surface, #3b4ea8);
  color: var(--color-primary-surface-content, #fff);
}
`;

type Refusal = { detail?: string; action?: { href?: string; label?: string } };

export function showAccessRefused(refusal: Refusal): void {
  if (typeof document === "undefined" || document.getElementById(ID)) return;
  // The page that settles the refusal must stay usable.
  const href = refusal.action?.href;
  if (href && window.location.pathname.startsWith(href)) return;

  const style = document.createElement("style");
  style.textContent = STYLE;
  const root = document.createElement("div");
  root.id = ID;
  root.setAttribute("role", "alertdialog");
  root.setAttribute("aria-labelledby", `${ID}-title`);

  const box = document.createElement("div");
  const title = document.createElement("h1");
  title.id = `${ID}-title`;
  title.textContent = "You cannot use this organization right now";
  const why = document.createElement("p");
  why.textContent = refusal.detail || "A rule of the organization refuses this request.";
  const actions = document.createElement("nav");
  if (href) {
    const go = document.createElement("a");
    go.href = href;
    go.textContent = refusal.action?.label || "Continue";
    go.setAttribute("data-primary", "");
    actions.appendChild(go);
  }
  const again = document.createElement("button");
  again.type = "button";
  again.textContent = "Try again";
  again.addEventListener("click", () => window.location.reload());
  const out = document.createElement("a");
  out.href = "/logout";
  out.textContent = "Sign out";
  actions.append(again, out);
  box.append(title, why, actions);
  root.append(style, box);
  document.body.appendChild(root);
}

let installed = false;

export function installAccessRefused(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  const inner = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await inner(...args);
    if (response.status === 403 && (response.headers.get("content-type") ?? "").includes("json")) {
      response
        .clone()
        .json()
        .then((body: Refusal & { code?: string }) => {
          if (body?.code === "access_refused") showAccessRefused(body);
        })
        .catch(() => {});
    }
    return response;
  };
}
